import {useForm, UseFormInput} from "@mantine/form";
import {Button, Stack, Textarea, TextInput} from "@mantine/core";
import {useContext} from "react";
import QRCodeContext from "../../../../../contexts/qr-code/QRCodeContext";
import VCardApi from "../../../../../api/vcard/VCardApi";
import Notifications from "../../../../../utils/notifications/Notifications";
import {useNavigate} from "react-router-dom";
import VCard from "../../../../../api/vcard/VCard";

type VCardInputDataProps = {}

type VCardForm = {
    firstName: string,
    lastName: string,
    summary?: string;
    phoneNumber?: string;
    emailAddress?: string;
    websiteUrl?: string;
    companyName?: string;
    position?: string;
}

/**
 * Converts string | undefined to appropriate value - undefined, if string is empty.
 * Otherwise, returns original string.
 * @since 0.2.0
 * @author Kacper Faber
 */
const convertIfEmpty = (e: string | undefined) => {
    if (!e) {
        return undefined;
    }
    return e.length > 0 ? e : undefined;
}

/**
 * Transform values to appropriate form.
 * For an example - phoneNumber will be undefined, if empty.
 * @author Kacper Faber
 * @since 0.2.1
 */
const transformValues = (values: VCardForm) => {
    return {
        ...values,
        summary: convertIfEmpty(values.summary),
        phoneNumber: convertIfEmpty(values.phoneNumber),
        emailAddress: convertIfEmpty(values.emailAddress),
        companyName: convertIfEmpty(values.companyName),
        position: convertIfEmpty(values.position),
        websiteUrl: convertIfEmpty(values.websiteUrl),
    };
};

// noinspection SpellCheckingInspection
/**
 * Generic mantine form definition.
 * It's
 * @author Kacper Faber
 * @since 0.2.1
 */
const formDef: UseFormInput<VCardForm> = {
    mode: 'uncontrolled',

    transformValues: transformValues,

    initialValues: {
        firstName: '',
        lastName: '',
        summary: '',
        phoneNumber: '',
        emailAddress: '',
        websiteUrl: '',
        companyName: '',
        position: ''
    },

    validate: {
        firstName: e => e.length > 3 ? null : 'Przynajmniej 3 znaki',
        lastName: e => e.length > 3 ? null : 'Przynajmniej 3 znaki'
    }
}

/**
 * @author Kacper Faber
 * @since 0.1.8
 * @param _props
 * @constructor
 */
function VCardInputData(_props: VCardInputDataProps) {
    /**
     * Form
     * @author Kacper Faber
     * @since 0.1.8
     */
    const form = useForm(formDef);

    /**
     * navigate function from react-router-dom
     * @author Kacper Faber
     * @since 0.1.9
     */
    const nav = useNavigate();

    /**
     * QRCodeContext
     * @author Kacper Faber
     * @since 0.2.1
     */
    const qrContext = useContext(QRCodeContext);

    /**
     * When entire QR code is created.
     * Show notification and redirect to index page.
     * @author Kacper Faber
     * @since 0.1.9
     */
    const onSubmitSuccess = () => {
        Notifications.onCreateSuccess();
        nav('/');
    };

    /**
     * On VCard is created, we need to create QRCode with this VCard.
     * @author Kacper Faber
     * @since 0.2.1
     */
    const onVCardCreated = async (vCard: VCard) => {
        const name = vCard.firstName + ' ' + vCard.lastName;
        return qrContext.create({key: '', kind: 'vcard', vCard: vCard.id, name });
    }

    /**
     * Call when form is going to be submitted.
     * Form is valid.
     * @author Kacper Faber
     * @since 0.1.8
     */
    const onSubmit = (values: VCardForm) => {
        VCardApi.create({...values})
            .then(onVCardCreated)
            .then(onSubmitSuccess)
            .catch(Notifications.onCreateFailed);
    }

    return (
        <form onSubmit={form.onSubmit(onSubmit)}>
            <Stack>
                <TextInput
                    required
                    placeholder="Imię"
                    key={form.key('firstName')}
                    {...form.getInputProps('firstName')}
                />

                <TextInput
                    required
                    placeholder="Nazwisko"
                    key={form.key('lastName')}
                    {...form.getInputProps('lastName')}
                />

                <Textarea
                    placeholder="Podsumowanie"
                    key={form.key('summary')}
                    {...form.getInputProps('summary')}
                />

                <TextInput
                    type="number"
                    placeholder="Numer telefonu"
                    description="Opcjonalny"
                    key={form.key('phoneNumber')}
                    {...form.getInputProps('phoneNumber')}
                />

                <TextInput
                    type="email"
                    placeholder="Adres E-mail"
                    description="Opcjonalny"
                    key={form.key('emailAddress')}
                    {...form.getInputProps('emailAddress')}
                />

                <TextInput
                    placeholder="Adres strony"
                    key={form.key('websiteUrl')}
                    {...form.getInputProps('websiteUrl')}
                />

                <TextInput
                    placeholder="Nazwa firmy"
                    key={form.key('companyName')}
                    {...form.getInputProps('companyName')}
                />

                <TextInput
                    placeholder="Stanowisko w firmie"
                    key={form.key('position')}
                    {...form.getInputProps('position')}
                />
            </Stack>

            <Button
                type="submit"
                children="ZAPISZ"
            />
        </form>
    );
}

export default VCardInputData;

// TODO: VCardInputData.tsx
//  Design